<template>
  <div>
    <div class="text-sm p-4 max-h-[4.25rem] bg-royal-200 dark:bg-dark-700 dark:text-dark-50 dark:border-dark-600 text-royal-900 font-medium flex items-center space-x-2 border-t border-royal-300/30" v-if="answer">
      <div class="grow line-clamp-2">
        <span class="text-royal-900/50 dark:text-dark-400">{{ $t('components.Post.question') }}</span> {{ stripTags(answer.content) }}
      </div>
      <div class="shrink-0" @click="() => emit('reset')">
        <IconBase name="cute/cross-fill" :width="16" />
      </div>
    </div>
    <div class="p-3.5 bg-charcoal-100 dark:bg-dark-950 max-h-36 z-10 overflow-auto" v-if="(suggestions.length > 0 || suggestionsLoading)">
      <SuggestionList
        :suggestions="suggestions"
        :is-loading="suggestionsLoading"
        @select="(v) => onSelectSuggestion(v)"
      />
    </div>
    <div class="border-t border-charcoal-200 dark:border-dark-800 space-y-2 p-2 w-full" v-if="post">
      <template v-if="currentUser?.isOnboarded2 && group && !group.isMember(currentUser?._id) && group.application !== 'open'">
        <div class="flex items-center space-x-2 min-h-12 text-charcoal-400 p-2">
          <p>{{ $t('components.Post.group.member') }}</p>
        </div>
      </template>
      <template v-else-if="currentUser?.isOnboarded2">
        <div class="flex items-end space-x-2">
          <div class="overflow-auto max-h-32 pl-2 grow min-h-12 flex flex-col justify-center dark:text-dark-50">
            <InputTextareaPaper
              is-mentions
              body-class="text-post"
              class="!border-none !rounded-none !bg-transparent" v-model="comment"
              :is-toolbar="false"
              @suggestions="v => suggestions = v"
              @suggestions-loading="v => suggestionsLoading = v"
              :placeholder="$t('actions.reply')"
              ref="$input"
            />
          </div>
          <ButtonBase size="base" color="slate" style-type="round" @click="onCommentSubmit" class="shrink-0" :is-loading="isLoading" :is-disabled="stripTags(comment).length <= 0">
            <IconBase name="cute/send-fill" :width="24" />
          </ButtonBase>
        </div>
      </template>
      <BlockCompleteProfile class="grow rounded-xl m-2" v-else />
    </div>
  </div>
  
</template>

<script lang="ts" setup>
import PostsStore from '@/store/posts'
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import GroupsStore from '@/store/groups'
import WindowStore from '@/store/window'
import { Post } from '@/classes'
import { notificationsRemove } from '@/utils/native/usePushNotifications'
import InputTextareaPaper from '../Input/InputTextareaPaper.vue'
import { Suggestion } from '../Suggestion/SuggestionList.vue'

const props = withDefaults(defineProps<{
  post: Post
  answer?: Post | null
  answerId?: string
}>(), {
  answer: null,
  answerId: undefined
})

const emit = defineEmits<{
  (e: 'end'): void
  (e: 'reset'): void
}>()

const { post, answer } = toRefs(props)

const userStore = UserStore()
const usersStore = UsersStore()
const windowStore = WindowStore()
const { titles } = storeToRefs(windowStore)
const postsStore = PostsStore()
const groupsStore = GroupsStore()
const { currentUser } = storeToRefs(userStore)

const route = useRoute()
const group = computed(() => post.value?.group ? groupsStore.getItem(post.value.group) : null)

// SUGGESTIONS

const $input = ref()
const suggestions: Ref<Suggestion[]> = ref([])
const suggestionsLoading = ref(false)

const onSelectSuggestion = (suggestion: Suggestion) => {
  if ($input.value) {
    suggestions.value = []
    $input.value.suggestionSelect(suggestion)
  }
}

// SUBMIT

const isLoading = ref(false)
const comment = ref('')

const onCommentSubmit = async () => {
  isLoading.value = true

  const response = await postsStore.create({
    content: comment.value,
    parent: props.answerId ? props.answerId : (answer.value?._id ? answer.value?._id : post.value?._id),
    isFollow: true
  })

  if (response.ok) {    
    comment.value = ''

    emit('end')

    useNuxtApp().$amplitude.logEvent(`Comment_Create`, {
      source: route.fullPath
    })
  }

  isLoading.value = false
}

defineExpose({
  focus: () => {
    if ($input.value) $input.value.focus()
  },
  reset: () => {
    if ($input.value) $input.value.reset()
  },
  suggestionSelect: (suggestion: Suggestion) => {
    if ($input.value) $input.value.suggestionSelect(suggestion)
  }
})

</script>